import { AiActionEnum, AiGovernance, AiProviderEnum } from '@common/schemas/ai-governance.schema';
import { IntelligenceTier } from '@common/schemas/novel.schema';
import { useFirebase } from '@/firebase';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

const TIERS: IntelligenceTier[] = ['eco', 'balanced', 'premium'];
const ACTIONS = AiActionEnum.options;
const PROVIDERS = AiProviderEnum.options;

export function AiMatrixEditor() {
  const { backend } = useFirebase();
  const [governance, setGovernance] = useState<AiGovernance | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadMatrix();
  }, [backend]);

  const loadMatrix = async () => {
    if (!backend) return;
    setLoading(true);
    try {
      const data = await backend.billing.getAiGovernance();
      if (data) {
        setGovernance(data);
      } else {
        // Initialize empty matrix
        const matrixObj: AiGovernance['matrix'] = {};
        const emptyMatrix: AiGovernance = { matrix: matrixObj, updatedAt: new Date().toISOString() };
        ACTIONS.forEach(action => {
          emptyMatrix.matrix[action] = {};
          TIERS.forEach(tier => {
            emptyMatrix.matrix[action]![tier] = { provider: 'cloudflare', model: '', tokenWeight: 1 };
          });
        });
        setGovernance(emptyMatrix);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError('Failed to load AI Matrix: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (action: string, tier: IntelligenceTier, field: string, value: string | number) => {
    if (!governance) return;
    setGovernance({
      ...governance,
      matrix: {
        ...governance.matrix,
        [action]: {
          ...(governance.matrix[action as keyof typeof governance.matrix] || {}),
          [tier]: {
            ...governance.matrix[action as keyof typeof governance.matrix]?.[tier],
            [field]: value
          }
        }
      }
    });
  };

  const handleSave = async () => {
    if (!backend || !governance) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const dataToSave = {
        ...governance,
        updatedAt: new Date().toISOString()
      };
      await backend.billing.saveAiGovernance(dataToSave);
      setSuccess('AI Matrix updated successfully.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError('Failed to save AI Matrix: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>AI Governance Matrix</Typography>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Card} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.05)' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
              {TIERS.map(tier => (
                <TableCell key={tier} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {tier.toUpperCase()}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ACTIONS.map(action => (
              <TableRow key={action}>
                <TableCell sx={{ fontWeight: 500, verticalAlign: 'top', pt: 2 }}>{action}</TableCell>
                {TIERS.map(tier => {
                  const config = governance?.matrix[action]?.[tier] || { provider: 'cloudflare', model: '', tokenWeight: 1 };
                  return (
                    <TableCell key={tier} sx={{ minWidth: 200 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Provider</InputLabel>
                          <Select
                            value={config.provider}
                            label="Provider"
                            onChange={(e) => handleChange(action, tier, 'provider', e.target.value)}
                          >
                            {PROVIDERS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Model ID"
                          size="small"
                          fullWidth
                          value={config.model}
                          onChange={(e) => handleChange(action, tier, 'model', e.target.value)}
                        />
                        <TextField
                          label="Token Weight"
                          type="number"
                          size="small"
                          fullWidth
                          value={config.tokenWeight}
                          onChange={(e) => handleChange(action, tier, 'tokenWeight', Number(e.target.value))}
                        />
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
